import React, { useState, useEffect } from "react"
import { useBlocks } from "app/regions/hooks/use-blocks"
import Label from "@kit/design/label"
import Input from "@kit/design/input"
import Toggle from "@kit/design/toggle"
import { Expander } from "app/regions/blocks/shared/expander"
import { OldButton } from "@kit/design/old-button"
import SelectMenu from "@kit/design/select-menu"
import { useFetch } from "hooks/use-new-fetch"
import { toast } from "@kit/design/toast"
import Stack from "@kit/design/stack"
import Icon from "@kit/design/icon"
import NoProducts from "app/shared/assets/no_products.svg"
import { ColorInput } from "@kit/design/color-picker"
import { useSavedColors } from "hooks/use-saved-colors"

const SettingToggle = ({ label, enabled, onClick, children }) => {
  return (
    <div className="flex flex-col border border-gray-200 rounded px-4 py-3">
      <div className="flex justify-between items-center">
        <Label>{label}</Label>
        <Toggle enabled={enabled} onClick={onClick} />
      </div>

      {children}
    </div>
  )
}

// This component is rendered in the sidebar after a Product embed is clicked.
// This is typically where you'd change the settings.
export function ProductEmbedSettings({ block_id }) {
  const { properties, update, remove } = useBlocks(block_id)
  const [productsList, setProductsList] = useState([])
  const [discountsList, setDiscountsList] = useState([])
  const [request, loading] = useFetch(`/editor/products`)
  const [discountsRequest, discountsLoading] = useFetch(`/editor/products`)
  const savedColors = useSavedColors()

  useEffect(() => {
    const fetchProducts = async () => {
      const { response, data } = await request.get()

      if (response.ok) {
        setProductsList(
          data["products"].map(p => {
            return { label: p.name, value: p.id }
          })
        )
      } else {
        toast({ type: "error", text: "Could not load products." })
      }
    }

    fetchProducts()
  }, [])

  useEffect(() => {
    const fetchDiscounts = async () => {
      const { response, data } = await discountsRequest.get({
        path: `/${properties.productId}/discounts`
      })

      if (response.ok) {
        setDiscountsList(data["discounts"])
      } else {
        toast({ type: "error", text: "Could not load discounts." })
      }
    }

    if (properties.includeDiscount) {
      fetchDiscounts()
    } else {
      update({ discountCode: null })
      setDiscountsList([])
    }
  }, [properties.productId, properties.includeDiscount])

  const handleProductSelection = e => {
    update({ productId: e.target.value, discountCode: null })
  }

  const handleDiscountSelection = e => {
    update({ discountCode: e.target.value })
  }

  const selectedProduct = productsList.find(
    product => product.value == properties.productId
  )

  return (
    <div className="px-4 pb-4">
      {!loading && (
        <>
          <div className="mb-4">
            {productsList && productsList.length > 0 ? (
              <Stack spacing={2}>
                <Label htmlFor="product_id">Product</Label>
                <SelectMenu
                  type="autocomplete"
                  key={`products-list-${properties.productId}`}
                  name="product_id"
                  className="w-full text-xs"
                  value={selectedProduct?.value}
                  options={productsList}
                  placeholder="Select a product"
                  onChange={handleProductSelection}
                />
              </Stack>
            ) : (
              <div className="border rounded mb-2 mt-2 p-4">
                <NoProducts
                  className="w-100"
                  height="150"
                  viewBox="0 100 400 50"
                />
                <p className="text-xl font-semibold pt-3">
                  You don&apos;t have any published products
                </p>
                <p className="py-3">
                  To create a product embed, you’ll need to publish a product
                  first.
                </p>
                <OldButton
                  is="secondary"
                  className="font-semibold text-gray-900 rounded pl-5"
                  href="/products"
                >
                  Products <Icon name="arrow-right" />
                </OldButton>
              </div>
            )}
          </div>

          {properties.productId && (
            <>
              <Stack spacing={4} className="mb-6">
                <SettingToggle
                  label="Include a discount"
                  enabled={properties.includeDiscount}
                  onClick={() =>
                    update({ includeDiscount: !properties.includeDiscount })
                  }
                >
                  {properties.includeDiscount && (
                    // The options for a discount code could be a group of radio
                    // buttons. We'd need to fetch the options here, too.
                    <div className="mt-4">
                      {!discountsLoading && discountsList.length == 0 && (
                        <div className="text-gray-700 text-sm">
                          This product doesn&apos;t have any discount codes.{" "}
                          <a
                            className="text-blue-500 font-semibold"
                            href={`/purchases/products/${properties.productId}/edit`}
                            target="_blank"
                            rel="noreferrer"
                          >
                            Edit product
                          </a>
                        </div>
                      )}
                      <Stack spacing={1}>
                        {discountsList.map(discount => {
                          return (
                            <tr
                              key={discount.id}
                              className="flex text-gray-800"
                            >
                              <td className="grow">
                                <input
                                  type="radio"
                                  className="ck-radio"
                                  id={`${discount.name}`}
                                  name="discount_name"
                                  value={discount.name}
                                  checked={
                                    properties.discountCode === discount.name
                                  }
                                  onChange={handleDiscountSelection}
                                />
                                <label
                                  className="uppercase"
                                  htmlFor={`${discount.name}`}
                                >
                                  {discount.name}
                                </label>
                              </td>
                              <td className="shrink">
                                {`-${discount.amount_formatted}`}
                              </td>
                            </tr>
                          )
                        })}
                      </Stack>
                    </div>
                  )}
                </SettingToggle>
                <SettingToggle
                  label="Go directly to checkout"
                  enabled={properties.goDirectlyToCheckout}
                  onClick={() =>
                    update({
                      goDirectlyToCheckout: !properties.goDirectlyToCheckout
                    })
                  }
                />
              </Stack>

              <Expander label="Elements" open={true}>
                <SettingToggle
                  label="Price"
                  enabled={properties.showPrice}
                  onClick={() => update({ showPrice: !properties.showPrice })}
                />
                <SettingToggle
                  label="Description"
                  enabled={properties.showDescription}
                  onClick={() =>
                    update({ showDescription: !properties.showDescription })
                  }
                />
                <SettingToggle
                  label="Image"
                  enabled={properties.showImage}
                  onClick={() => update({ showImage: !properties.showImage })}
                />
                <SettingToggle
                  label="Button"
                  enabled={properties.showButton}
                  onClick={() => update({ showButton: !properties.showButton })}
                >
                  {properties.showButton && (
                    <div className="mt-4">
                      <Stack spacing={2}>
                        <Label htmlFor="name">Button text</Label>
                        <Input
                          value={properties.buttonText}
                          onChange={e => update({ buttonText: e.target.value })}
                          placeholder="Default"
                        />
                        <Label htmlFor="name">Background color</Label>
                        <ColorInput
                          value={properties.buttonColor}
                          onChange={color => update({ buttonColor: color.hex })}
                          savedColors={savedColors}
                        />
                        <Label htmlFor="name">Text color</Label>
                        <ColorInput
                          value={properties.buttonTextColor}
                          onChange={color =>
                            update({ buttonTextColor: color.hex })
                          }
                          savedColors={savedColors}
                        />
                      </Stack>
                    </div>
                  )}
                </SettingToggle>
              </Expander>
            </>
          )}
        </>
      )}
      <OldButton
        size="sm"
        is="secondary"
        className="mt-4 ml-auto block"
        onClick={() => remove(block_id)}
        icon="delete"
      >
        Remove
      </OldButton>
    </div>
  )
}
